import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { messages } from '../../i18n/messages';

import { SCOPES, SOCIAL_MEDIA_TYPES } from '../../constants';
import CustomLink from '../CustomLink';
import IconButton from '../IconButton';

const LINKEDIN_BASE_URL = 'https://www.linkedin.com/in/';
const FACEBOOK_BASE_URL = 'https://www.facebook.com/';
const TWITTER_BASE_URL = 'https://www.twitter.com/';

export const propTypes = {

	/**
	 * The type of social media.
	 */
	type: PropTypes.oneOf([
		SOCIAL_MEDIA_TYPES.EMAIL,
		SOCIAL_MEDIA_TYPES.FACEBOOK,
		SOCIAL_MEDIA_TYPES.LINKEDIN,
		SOCIAL_MEDIA_TYPES.TWITTER,
		SOCIAL_MEDIA_TYPES.MESSAGE,
	]).isRequired,

	/**
	 * The user's display name.
	 */
	displayName: PropTypes.string.isRequired,

	/**
	 * True, if the social media icon is for the current user.
	 *
	 * NOTE: Do not use the "message" type if "isSelf" is true.
	 */
	isSelf: PropTypes.bool.isRequired,

	/**
	 * The social media URL.
	 */
	url: PropTypes.string.isRequired,

	/**
	 * The link scope.
	 */
	scope: PropTypes.oneOf([SCOPES.SESSION, SCOPES.ORGANIZATION]).isRequired,

	/**
	 * The link scope ID.
	 */
	scopeId: PropTypes.string.isRequired,

	/**
	 * Custom callback function to handle clicking the link.
	 *
	 * NOTE: This is used to navigate via react-router
	 *       for internal chat message links.
	 */
	onClick: PropTypes.func,

};

const defaultProps = {
	isSelf: false,
};

/**
 * Shared component to render a basic TechChange-style social media icon.
 *
 * @param {Object} props The props.
 *
 * @returns {Object} The rendered component.
 */
const SocialIcon = (props) => {

	const {
		type,
		displayName,
		isSelf,
		url,
		scope,
		scopeId,
		onClick,
		...restProps
	} = props;

	const intl = useIntl();

	let intlTypeMsg;

	let intlTitleMsg;

	let className = `tc-social-icon ${restProps.className}`;

	let icon;

	switch (type) {
	case SOCIAL_MEDIA_TYPES.EMAIL:

		className += ' tc-social-icon-email';
		icon = 'fas fa-envelope fa-lg';

		intlTypeMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TYPE_EMAIL;
		intlTitleMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TITLE;

		break;

	case SOCIAL_MEDIA_TYPES.FACEBOOK:

		className += ' tc-social-icon-facebook';
		icon = 'fab fa-facebook-f fa-lg';

		intlTypeMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TYPE_FACEBOOK;
		intlTitleMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TITLE;

		break;

	case SOCIAL_MEDIA_TYPES.LINKEDIN:

		className += ' tc-social-icon-linkedin';
		icon = 'fab fa-linkedin-in fa-lg';

		intlTypeMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TYPE_LINKEDIN;
		intlTitleMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TITLE;

		break;

	// Twitter no longer supports share count totals
	case SOCIAL_MEDIA_TYPES.TWITTER:

		className += ' tc-social-icon-twitter';
		icon = 'fab fa-twitter fa-lg';

		intlTypeMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TYPE_TWITTER;
		intlTitleMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TITLE;

		break;

	case SOCIAL_MEDIA_TYPES.MESSAGE:

		className += ' tc-social-icon-message';
		icon = 'fas fa-comments-alt fa-lg';

		intlTypeMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_TYPE_DIRECT_MESSAGE;
		intlTitleMsg = messages.TC_COMPONENTS_SOCIAL_MEDIA_CONNECT_DIRECT_MESSAGE_TITLE;

		break;

	default:

		console.warn(`The social media type=${type} is not supported by the SocialIcon component...`);
		return null;

	}

	// Format the internationalized version of the social media type text
	const intlType = intl.formatMessage(intlTypeMsg);

	// Format the internationalized version of the social media title text
	let title;

	if (isSelf) {

		title = intl.formatMessage(
			messages.TC_COMPONENTS_SOCIAL_MEDIA_VIEW_SELF_PROFILE_TITLE,
			{ type: intlType }
		);

	} else {

		title = intl.formatMessage(intlTitleMsg, {
			name: displayName,
			type: intlType,
		});

	}

	// Check if the icon button includes a link...

	let href = url;

	// Check if the social icon is for an email URL
	if (type === SOCIAL_MEDIA_TYPES.EMAIL) {

		if (href.indexOf('mailto:') !== 0) {
			href = `mailto:${href}`;
		}

	// Check if the social icon is for a facebook URL
	} else if (type === SOCIAL_MEDIA_TYPES.FACEBOOK) {

		if (href.indexOf(FACEBOOK_BASE_URL) !== 0) {
			href = `${FACEBOOK_BASE_URL}${href.charAt(0) === '/' ? href.slice(1) : href}`;
		}

	// Check if the social icon is for a linkedin URL
	} else if (type === SOCIAL_MEDIA_TYPES.LINKEDIN) {

		if (href.indexOf(LINKEDIN_BASE_URL) !== 0) {
			href = `${LINKEDIN_BASE_URL}${href.charAt(0) === '/' ? href.slice(1) : href}`;
		}

	// Check if the social icon is for a twitter URL
	} else if (type === SOCIAL_MEDIA_TYPES.TWITTER) {

		if (href.indexOf(TWITTER_BASE_URL) !== 0) {
			href = `${TWITTER_BASE_URL}${href.charAt(0) === '/' ? href.slice(1) : href}`;
		}

	}

	return (
		<IconButton
			{...restProps}
			className={className}
			icon={icon}
			aria-label={title}
			title={title}
			tag={
				<CustomLink
					url={href}
					target="_blank"
					scope={scope}
					scopeId={scopeId} />
			}
			onClick={onClick} />
	);

};

SocialIcon.defaultProps = defaultProps;
SocialIcon.propTypes = propTypes;

export default SocialIcon;
